import type { License } from "../../../../../../store/reseller-admin/licensesStore";
import type { ThemeClasses } from "../types";
import { BILLING_MODELS, BILLING_CYCLES, STATUS_OPTIONS } from "../types";

interface LicenseGridCardProps {
  license: License;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onDetails: (license: License) => void;
  onEdit: (license: License) => void;
  onDelete: (license: License) => void;
}

export default function LicenseGridCard({
  license,
  themeClasses,
  isDarkMode,
  onDetails,
  onEdit,
  onDelete,
}: LicenseGridCardProps) {
  const statusOpt = STATUS_OPTIONS.find((s) => s.value === license.status);
  const billingModel = BILLING_MODELS.find((b) => b.value === license.billing_model);
  const billingCycle = BILLING_CYCLES.find((c) => c.value === license.billing_cycle);

  const getPrice = () => {
    if (license.billing_model === "per_unit" && license.price_per_unit != null) {
      return `$${license.price_per_unit.toFixed(2)} ${license.currency}/unidad`;
    }
    if (license.billing_model === "flat" && license.flat_price != null) {
      return `$${license.flat_price.toFixed(2)} ${license.currency}`;
    }
    return "Por definir";
  };

  const statusColors: Record<string, string> = {
    active: isDarkMode ? "bg-green-900/40 text-green-400 border-green-700" : "bg-green-50 text-green-700 border-green-200",
    inactive: isDarkMode ? "bg-red-900/40 text-red-400 border-red-700" : "bg-red-50 text-red-700 border-red-200",
    draft: isDarkMode ? "bg-yellow-900/40 text-yellow-400 border-yellow-700" : "bg-yellow-50 text-yellow-700 border-yellow-200",
  };

  const entityLimits = license.entity_limits || {};
  const activeLimits = Object.entries(entityLimits).filter(
    ([, val]) => val != null && val > 0,
  );

  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-2xl p-5 hover:shadow-lg transition-all cursor-pointer group`}
      onClick={() => onDetails(license)}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className={`font-semibold text-lg ${themeClasses.text} truncate`}>
            {license.name}
          </h3>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border mt-1 ${statusColors[license.status] || statusColors.draft}`}>
            {statusOpt?.label || license.status}
          </span>
        </div>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(license); }}
            className={`p-1.5 rounded-lg ${themeClasses.buttonHover} ${themeClasses.buttonText} hover:text-blue-500`}
            title="Editar"
          >
            <i className="ri-pencil-line"></i>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(license); }}
            className={`p-1.5 rounded-lg ${themeClasses.buttonHover} ${themeClasses.buttonText} hover:text-red-500`}
            title="Desactivar"
          >
            <i className="ri-delete-bin-line"></i>
          </button>
        </div>
      </div>

      {/* Description */}
      {license.description && (
        <p className={`text-sm ${themeClasses.textSecondary} line-clamp-2 mb-3`}>
          {license.description}
        </p>
      )}

      {/* Price */}
      <div className={`flex items-center gap-2 mb-3 p-2.5 rounded-xl ${isDarkMode ? "bg-dark-bg" : "bg-gray-50"}`}>
        <i className={`ri-money-dollar-circle-line text-lg ${isDarkMode ? "text-primary-400" : "text-primary-600"}`}></i>
        <div>
          <p className={`font-bold text-sm ${themeClasses.text}`}>{getPrice()}</p>
          <p className={`text-xs ${themeClasses.textSecondary}`}>
            {billingModel?.label} · {billingCycle?.label}
          </p>
        </div>
      </div>

      {/* Apps */}
      {license.apps && license.apps.length > 0 && (
        <div className="mb-3">
          <p className={`text-xs font-medium ${themeClasses.textSecondary} mb-1`}>Apps incluidas:</p>
          <div className="flex flex-wrap gap-1">
            {license.apps.slice(0, 3).map((app) => (
              <span
                key={app}
                className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? "bg-blue-900/30 text-blue-400" : "bg-blue-50 text-blue-700"}`}
              >
                {app}
              </span>
            ))}
            {license.apps.length > 3 && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? "bg-dark-border text-gray-400" : "bg-gray-100 text-gray-500"}`}>
                +{license.apps.length - 3}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Entity limits summary */}
      {activeLimits.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {activeLimits.slice(0, 4).map(([key, val]) => (
            <span
              key={key}
              className={`text-xs px-2 py-1 rounded-lg ${isDarkMode ? "bg-dark-border text-gray-300" : "bg-gray-100 text-gray-600"}`}
            >
              {val} {key}
            </span>
          ))}
          {activeLimits.length > 4 && (
            <span className={`text-xs px-2 py-1 rounded-lg ${isDarkMode ? "bg-dark-border text-gray-400" : "bg-gray-100 text-gray-500"}`}>
              +{activeLimits.length - 4} más
            </span>
          )}
        </div>
      )}
    </div>
  );
}
